/**
 * PipelineWritesTab — history of Task Pipeline writeback decisions.
 *
 * Notion cannot derive a Task's stage from its Tickets itself (automation
 * formulas read neither relation nor formula properties, and automations do
 * not chain), so the 10-minute PM snapshot does it and writes the result to
 * the Notion `Pipeline` select. This tab is the audit trail of those writes.
 *
 * Rows appear only when the derived stage DISAGREED with the board, so a quiet
 * table means the board is in sync — not that nothing ran.
 *
 * When writeback is disabled, every row is a `dry_run`: the change that WOULD
 * have been made. That is the intended way to review the behavior on real data
 * before enabling it.
 */

import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useState, type ReactElement } from 'react';

import { usePmPipelineWrites } from '@/hooks/usePmPipelineWrites';
import type {
  PmPipelineWrite,
  PmPipelineWriteStatus,
} from '@/types/pm_notification';

const STATUS_COLOR: Record<
  PmPipelineWriteStatus,
  'success' | 'info' | 'error'
> = {
  applied: 'success',
  dry_run: 'info',
  failed: 'error',
};

const STATUS_LABEL: Record<PmPipelineWriteStatus, string> = {
  applied: 'applied',
  dry_run: 'dry run',
  failed: 'failed',
};

const STATUS_OPTIONS: Array<{ value: PmPipelineWriteStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'applied', label: 'Applied' },
  { value: 'dry_run', label: 'Dry run' },
  { value: 'failed', label: 'Failed' },
];

function notionUrl(notionId: string): string {
  return `https://www.notion.so/${notionId.replaceAll('-', '')}`;
}

function StatusChip({ status }: { status: PmPipelineWriteStatus }): ReactElement {
  return (
    <Chip
      label={STATUS_LABEL[status]}
      color={STATUS_COLOR[status]}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );
}

/** from → to, rendered as one glanceable unit. */
function StageChange({ row }: { row: PmPipelineWrite }): ReactElement {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {row.from_pipeline ?? '(unset)'}
      </Typography>
      <ArrowForwardIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {row.to_pipeline}
      </Typography>
    </Stack>
  );
}

function PipelineWriteRow({ row }: { row: PmPipelineWrite }): ReactElement {
  const [open, setOpen] = useState(false);
  const evidence = row.ticket_evidence ?? [];

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 40, p: 0.5 }}>
          <IconButton
            size="small"
            aria-label={open ? 'Collapse row' : 'Expand row to see ticket evidence'}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            {new Date(row.decided_at).toLocaleString()}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.task_name ?? '(untitled)'}
          </Typography>
        </TableCell>
        <TableCell>
          <StageChange row={row} />
        </TableCell>
        <TableCell>
          <StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <Tooltip title="Open task in Notion">
            <IconButton
              component="a"
              href={notionUrl(row.notion_id)}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label={`Open Notion task ${row.notion_id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 4, py: 2, bgcolor: 'background.deeper' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.75, display: 'block' }}
              >
                Ticket states this decision was computed from
              </Typography>
              {evidence.length > 0 ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {evidence.map((e, i) => (
                    <Chip
                      // reason: evidence is a positional snapshot with no stable id
                      key={`${e.stage}-${i}`}
                      size="small"
                      variant="outlined"
                      label={e.is_design ? `${e.stage} · design` : e.stage}
                      color={e.is_design ? 'secondary' : 'default'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No evidence recorded.
                </Typography>
              )}
              {row.error && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, mb: 0.5, display: 'block' }}
                  >
                    Why it did not apply
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    {row.error}
                  </Typography>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function PipelineWritesTab(): ReactElement {
  const [statusFilter, setStatusFilter] = useState<PmPipelineWriteStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, summary, isLoading, error, refetch } = usePmPipelineWrites({
    status: statusFilter || undefined,
    page,
  });

  const rows = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Box>
      {summary && !summary.writeback_enabled && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          Writeback is <strong>disabled</strong> — Sherlock is not changing the
          Notion board. Every row below is a dry run showing what it would have
          changed. Enable with <code>SHERLOCK_PM_PIPELINE_WRITEBACK_ENABLED=true</code>{' '}
          once this log looks right.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="pipeline-status-filter-label">Status</InputLabel>
          <Select
            labelId="pipeline-status-filter-label"
            id="pipeline-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as PmPipelineWriteStatus | '');
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {summary && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="success" label={`${summary.applied} applied`} />
            <Chip size="small" color="info" label={`${summary.dry_run} dry run`} />
            <Chip
              size="small"
              color={summary.failed > 0 ? 'error' : 'default'}
              label={`${summary.failed} failed`}
            />
          </Stack>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <IconButton size="large" aria-label="Refresh pipeline write log" onClick={refetch}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && !data && (
        <Box role="status" aria-label="Loading pipeline writes">
          {Array.from({ length: 6 }).map((_, i) => (
            // reason: stable static list — key-by-index is fine
            <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No pipeline changes recorded.
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Rows appear only when a Task&apos;s stage disagrees with its tickets,
            so an empty log means the board is in sync.
          </Typography>
        </Box>
      )}

      {rows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="Task pipeline writeback log">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Decided</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Pipeline change</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <PipelineWriteRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          aria-label="Pipeline write log pagination"
        />
      )}
    </Box>
  );
}
